<template>
  <div class="plain-dashboard">
    <div class="plain-dashboard-heading">
      <div>
        <h1>Dashboard</h1>
        <p>Review your workspace and open the modules available to your account.</p>
      </div>
      <div class="plain-dashboard-actions">
        <button v-if="notificationPromptVisible" type="button" class="plain-button plain-button-outline" @click="handleEnableNotifications">
          <BellRing :size="17" />
          {{ notificationLoading ? "Enabling..." : "Enable alerts" }}
        </button>
        <button type="button" class="plain-button plain-button-primary" @click="handleRefresh">
          <RefreshCw :size="17" />
          Refresh
        </button>
      </div>
    </div>

    <div v-if="isLoading" class="plain-state-card">
      <span class="plain-spinner"></span>
      <strong>Loading workspace</strong>
      <p>Your permitted modules and current alerts are being prepared.</p>
    </div>

    <div v-else-if="error" class="plain-state-card is-error">
      <AlertCircle :size="28" />
      <strong>Something went wrong</strong>
      <p>{{ error }}</p>
      <button type="button" class="plain-button plain-button-primary" @click="loadModules">Try again</button>
    </div>

    <template v-else>
      <section v-if="user?.type === 'provider'" class="plain-metric-card provider-traffic-card" aria-label="Provider enrollee traffic" :aria-busy="providerTrafficLoading">
        <div class="provider-traffic-summary">
          <div class="plain-metric-label"><span>Enrollee traffic</span><Users :size="17" /></div>
          <strong>{{ providerTraffic ? Number(providerTraffic.total).toLocaleString() : '—' }}</strong>
          <p>{{ providerTraffic && !providerTraffic.total ? 'No visits recorded in the last six months' : 'Visits over the last six months' }}</p>
          <small v-if="providerTraffic">Through {{ providerTraffic.through }} · Repeat visits counted separately</small>
        </div>
        <div class="provider-traffic-bars" v-if="providerTraffic">
          <div class="provider-traffic-row" v-for="(month, index) in providerTraffic.months" :key="month.month">
            <span>{{ month.label }}</span>
            <div class="provider-traffic-track" role="meter" :aria-label="`${month.label} enrollee visits`" :aria-valuenow="month.visits" :aria-valuemin="0" :aria-valuemax="providerTrafficMax">
              <span :style="{ width: `${month.visits / providerTrafficMax * 100}%`, backgroundColor: trafficColors[index % trafficColors.length] }"></span>
            </div>
            <strong>{{ month.visits }}</strong>
          </div>
        </div>
        <p v-else class="provider-traffic-empty">{{ providerTrafficLoading ? 'Loading enrollee traffic…' : 'Enrollee traffic could not be loaded.' }} <button v-if="!providerTrafficLoading" type="button" class="plain-button plain-button-outline" @click="loadProviderTraffic">Retry</button></p>
      </section>
      <section v-else-if="canViewReport" class="dashboard-report-overview" aria-label="Executive report overview">
        <div class="dashboard-report-caption">
          <span>{{ reportScope }} · {{ reportSnapshot?.year || 'Current / latest' }}</span>
          <span>{{ reportSnapshot?.updatedAt ? `Updated ${new Date(reportSnapshot.updatedAt).toLocaleString()}` : reportLoading ? 'Loading report summary…' : 'Report summary unavailable' }}</span>
        </div>
        <div class="plain-metric-grid">
          <router-link v-for="metric in reportMetrics" :key="metric.label" to="/report" class="plain-metric-card dashboard-report-card" :class="metric.tone" :aria-label="`${metric.label}: open reports`">
            <div class="plain-metric-label"><span>{{ metric.label }}</span><component :is="metric.icon" :size="17" /></div>
            <strong>{{ metric.value }}</strong>
            <p>{{ metric.detail }}</p>
          </router-link>
        </div>
      </section>
      <section v-else class="plain-metric-grid" aria-label="Workspace overview">
        <article class="plain-metric-card">
          <div class="plain-metric-label"><span>Available modules</span><Boxes :size="17" /></div>
          <strong>{{ tabs.length }}</strong>
          <p>Modules assigned to this account</p>
        </article>
        <article class="plain-metric-card">
          <div class="plain-metric-label"><span>Items requiring attention</span><BellRing :size="17" /></div>
          <strong>{{ attentionTotal }}</strong>
          <p>{{ attentionModules }} module{{ attentionModules === 1 ? "" : "s" }} reporting updates</p>
        </article>
        <article class="plain-metric-card">
          <div class="plain-metric-label"><span>Access profile</span><ShieldCheck :size="17" /></div>
          <strong class="is-text">{{ accessLabel }}</strong>
          <p>Current workspace authority</p>
        </article>
        <article class="plain-metric-card">
          <div class="plain-metric-label"><span>Notifications</span><BellRing :size="17" /></div>
          <strong class="is-text">{{ notificationState }}</strong>
          <p>Browser alert status</p>
        </article>
      </section>

      <section class="plain-workspace-section">
        <div class="plain-section-heading">
          <div>
            <h2>Workspace modules</h2>
            <p>Open a module to continue your operational work.</p>
          </div>
          <label class="plain-module-filter">
            <Search :size="17" />
            <input v-model.trim="moduleSearch" type="search" placeholder="Filter modules..." aria-label="Filter modules" />
          </label>
        </div>

        <div v-if="filteredTabs.length" class="plain-module-grid">
          <article v-for="tab in filteredTabs" :key="tab.id" class="plain-module-card">
            <div class="plain-module-card-top">
              <span class="plain-module-icon"><component :is="moduleIcon(tab.to)" :size="21" /></span>
              <button type="button" class="plain-button plain-button-outline plain-open-button" @click="navigateToModule(tab.to)">Open</button>
            </div>
            <div class="plain-module-copy">
              <h3>{{ tab.title }}</h3>
              <p>{{ tab.description || "Open this module to manage assigned operations." }}</p>
            </div>
            <span v-if="tab.notification" class="plain-module-notice">{{ tab.notification }}</span>
          </article>
        </div>

        <div v-else class="plain-empty-state">
          <Boxes :size="28" />
          <h3>{{ moduleSearch ? "No matching modules" : "No modules available" }}</h3>
          <p>{{ moduleSearch ? "Try a different module name." : "Your profile does not currently have active workspace modules." }}</p>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { onMounted, onUnmounted, ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { AlertCircle, BellRing, Boxes, RefreshCw, Search, ShieldCheck, Users, Wallet, Activity, ChartNoAxesCombined } from "@lucide/vue";
import { readDashboardReportSnapshot, loadDashboardReportSnapshot } from "@/composables/dashboardReportSnapshot";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import { siloMode } from "@/laravel/config";
import { addModules } from "@/composables/cache/modules";
import { clearCacheOnly } from "@/composables/cache/logout";
import brand from "@/whitelabel/brand.config";
import { moduleIcon } from "@/ui/templates/plain-admin/module-icons";
import {
  clearCachedPayload,
  getCachedPayload,
  setCachedPayload,
} from "@/composables/requestOptimization";
import {
  isMessagingReady,
  listenForForegroundMessages,
  registerNotificationToken,
  syncNotificationToken,
} from "@/composables/useFirebaseMessaging";
import {
  reconcileAuthorizationHomeCount,
  registerAuthorizationRealtimeTarget,
} from "@/composables/authorizationRealtimeCoordinator";

const COLOR_PALETTE = ["teal", "blue", "indigo", "purple"];
const MODULE_COLORS = COLOR_PALETTE.map(color => `${color}-accent`);
const HOME_REFRESH_THROTTLE = 15 * 60 * 1000;
const STARTER_CACHE_TTL = HOME_REFRESH_THROTTLE;

export default {
  name: "HomeView",
  components: { AlertCircle, BellRing, Boxes, RefreshCw, Search, ShieldCheck, Users },
  setup() {
    const router = useRouter();
    const store = useStore();

    const tabs = ref([]);
    const isLoading = ref(true);
    const error = ref(null);
    const notificationReady = ref(false);
    const notificationPermission = ref("default");
    const notificationLoading = ref(false);
    const moduleSearch = ref("");

    const user = computed(() => store.state.user || null);
    const providerTraffic = ref(null);
    const providerTrafficLoading = ref(false);
    const trafficColors = ['#2563eb', '#0d9488', '#7c3aed', '#d97706', '#0891b2', '#db2777'];
    const providerTrafficMax = computed(() => Math.max(1, ...(providerTraffic.value?.months || []).map(month => Number(month.visits))));
    const loadProviderTraffic = async () => {
      if (user.value?.type !== 'provider' || providerTrafficLoading.value) return;
      const owner = `${user.value.id}:${user.value.provider_id || user.value.account?.id}`;
      const key = `provider-dashboard-traffic:${owner}`;
      const cached = getCachedPayload(key);
      if (cached) { providerTraffic.value = cached; return; }
      providerTrafficLoading.value = true;
      try {
        const { res } = await getItems('operator/provider/traffic');
        if (user.value?.type === 'provider' && `${user.value.id}:${user.value.provider_id || user.value.account?.id}` === owner && res.value?.data) {
          providerTraffic.value = res.value.data;
          setCachedPayload(key, res.value.data, HOME_REFRESH_THROTTLE);
        }
      } finally { providerTrafficLoading.value = false; }
    };
    const userModules = computed(() =>
      Array.isArray(user.value?.modules) ? user.value.modules : []
    );

    const notificationPromptVisible = computed(() =>
      notificationReady.value &&
      notificationPermission.value === "default" &&
      userModules.value.includes("auth")
    );
    const filteredTabs = computed(() => {
      const term = moduleSearch.value.toLowerCase();
      return term
        ? tabs.value.filter((tab) => `${tab.title} ${tab.description || ""}`.toLowerCase().includes(term))
        : tabs.value;
    });
    const attentionModules = computed(() => tabs.value.filter((tab) => tab.notification).length);
    const attentionTotal = computed(() => tabs.value.reduce((total, tab) => {
      const count = Number.parseInt(String(tab.notification || "").replace(/\D/g, ""), 10);
      return total + (Number.isFinite(count) ? count : 0);
    }, 0));
    const accessLabel = computed(() => user.value?.designation || user.value?.account?.name || "Workspace user");
    const notificationState = computed(() => notificationPermission.value === "granted" ? "Enabled" : "Action needed");
    const canViewReport = computed(() => userModules.value.includes("report"));
    const reportSnapshot = ref(null);
    const reportLoading = ref(false);
    const reportScope = computed(() => ({ hmo: "Private", retail: "Retail", tpa: "TPA" }[reportSnapshot.value?.category || "hmo"] || "Report"));
    const money = (value) => `NGN ${Number(value || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const reportMetrics = computed(() => {
      const s = reportSnapshot.value?.summary;
      return [
        { label: "Clients", value: s ? Number(s.clients_count || 0).toLocaleString() : "—", detail: "Clients in reporting scope", tone: "is-clients", icon: Users },
        { label: "Premium Received", value: s ? money(s.invoice_paid) : "—", detail: "Premium collected", tone: "is-premium", icon: Wallet },
        { label: "Gross Utilization", value: s ? money(s.gross_utilization ?? s.utilization_sent) : "—", detail: s ? `${money(s.real_utilization)} real` : "Billed claims", tone: "is-utilization", icon: Activity },
        { label: "Gross MLR", value: s ? `${s.gross_mlr ?? s.utilization_rate ?? 0}%` : "—", detail: s ? `${s.real_mlr ?? 0}% real` : "Medical loss ratio", tone: s && Number(s.gross_mlr ?? s.utilization_rate) >= 100 ? "is-risk" : "is-mlr", icon: ChartNoAxesCombined },
      ];
    });
    const loadReportSnapshot = async () => {
      if (!canViewReport.value) return;
      reportSnapshot.value = readDashboardReportSnapshot();
      reportLoading.value = true;
      try { reportSnapshot.value = await loadDashboardReportSnapshot(); }
      finally { reportLoading.value = false; }
    };
    let starterRequestInFlight = false;

    onMounted(() => {
      document.title = "Admin Dashboard";
      loadModules();
      prepareNotifications();
      loadReportSnapshot();
      loadProviderTraffic();
    });

    const getProfileDisplay = (fullName) => {
      if (!fullName) return "";
      const name = fullName.trim().split(/[.\s]+/)[0];
      return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    };

    const loadModules = async () => {
      try {
        isLoading.value = true;
        error.value = null;
        const sourceTabs = addModules();
        const isFreshSignin = store.state.dashboardFreshSignin;

        if (user.value) {
          tabs.value = sourceTabs
            .filter(tab =>
              tab.to !== "help" && userModules.value.includes(tab.to) &&
              tab.type === user.value.type
            )
            .map((tab, index) => ({
              ...tab,
              bgColor: MODULE_COLORS[index % MODULE_COLORS.length],
              notification: null
            }));

          const lastRefresh = Number(window.localStorage.getItem(homeRefreshKey()) || 0);
          const shouldRefresh = isFreshSignin || !lastRefresh || Date.now() - lastRefresh >= HOME_REFRESH_THROTTLE;
          const refreshed = await loadNotifications({ force: shouldRefresh });

          if (shouldRefresh && refreshed) {
            window.localStorage.setItem(homeRefreshKey(), String(Date.now()));
          }
        }
      } catch (err) {
        error.value = "Failed to load modules";
      } finally {
        store.commit("loadDashboardFreshSignin", false);
        isLoading.value = false;
      }
    };

    const applyNotifications = (items) => {
      const map = new Map(
        items.map(item => [item.name, item.message])
      );
      tabs.value = tabs.value.map(tab => ({
        ...tab,
        notification: map.get(tab.to) || null
      }));
    };

    const incrementAuthorizationCount = () => {
      let updated = false;
      let nextMessage = null;

      tabs.value = tabs.value.map(tab => {
        if (tab.to !== "auth") return tab;

        const currentCount = Number.parseInt(tab.notification, 10) || 0;
        nextMessage = `${currentCount + 1} Pre-auth request(s) pending`;
        updated = true;
        return {
          ...tab,
          notification: nextMessage,
        };
      });

      const cacheKey = starterCacheKey();
      const cachedPayload = cacheKey ? getCachedPayload(cacheKey) : null;

      if (updated && Array.isArray(cachedPayload)) {
        const hasAuth = cachedPayload.some(item => item.name === "auth");
        const nextPayload = cachedPayload.map(item => item.name === "auth"
          ? { ...item, message: nextMessage }
          : item
        );

        if (!hasAuth) {
          nextPayload.push({
            id: nextPayload.length + 1,
            name: "auth",
            message: nextMessage,
          });
        }

        setCachedPayload(cacheKey, nextPayload, STARTER_CACHE_TTL);
      }

      return updated;
    };

    const applyAuthNotification = (items) => {
      const auth = items.find(item => item.name === "auth");
      if (!auth) return;

      tabs.value = tabs.value.map(tab => tab.to === "auth"
        ? { ...tab, notification: auth.message || null }
        : tab
      );
    };

    const starterCacheKey = () => {
      if (!user.value?.id) {
        return null;
      }

      const authSource = siloMode ? "easyhmo" : "oneprovider";
      return `operator-starter:${user.value.id}:${authSource}`;
    };

    const homeRefreshKey = () => `${starterCacheKey()}:last-home-refresh`;

    const loadNotifications = async ({ force = false, authOnly = false } = {}) => {
      try {
        const cacheKey = starterCacheKey();

        if (!cacheKey || starterRequestInFlight) return false;

        const cachedPayload = !force ? getCachedPayload(cacheKey) : null;
        if (cachedPayload) {
          authOnly ? applyAuthNotification(cachedPayload) : applyNotifications(cachedPayload);
          return true;
        }

        starterRequestInFlight = true;
        const authSource = siloMode ? "easyhmo" : "oneprovider";
        const { res } = await getItems(`operator/starter?u=${user.value.id}&auth_source=${authSource}`);

        if (res.value?.data) {
          authOnly ? applyAuthNotification(res.value.data) : applyNotifications(res.value.data);
          reconcileAuthorizationHomeCount();
          setCachedPayload(cacheKey, res.value.data, STARTER_CACHE_TTL);
          return true;
        }
      } catch (err) {
        // Notification counts should not block dashboard access.
      } finally {
        starterRequestInFlight = false;
      }

      return false;
    };

    const unregisterRealtimeTarget = registerAuthorizationRealtimeTarget("home", {
      incrementAuthorizationCount,
    });
    onUnmounted(unregisterRealtimeTarget);

    const handleRefresh = async () => {
      if (!siloMode) {
        await postItem("integration/oneprovider/claims/sync", { force: true });
      }

      clearCachedPayload(starterCacheKey());
      clearCacheOnly();
      router.push({ name: "loading" });
    };

    const handleSignOut = () => {
      router.push({ name: "logout" });
    };

    const navigateToModule = (route) => router.push({ name: route });

    const prepareNotifications = async () => {
      notificationReady.value = await isMessagingReady();
      notificationPermission.value = "Notification" in window ? Notification.permission : "denied";

      if (!notificationReady.value) {
        return;
      }

      if (notificationPermission.value === "granted") {
        await syncNotificationToken();
      }

      listenForForegroundMessages((payload) => {
        const title = payload?.notification?.title || brand.productName;
        const body = payload?.notification?.body || "New notification received.";

        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(title, {
            body,
            icon: "/img/icons/android-chrome-192x192.png",
          });
        }
      });
    };

    const handleEnableNotifications = async () => {
      notificationLoading.value = true;
      const result = await registerNotificationToken();
      notificationPermission.value = "Notification" in window ? Notification.permission : result.status;
      notificationLoading.value = false;
    };

    return {
      brand,
      providerTraffic,
      providerTrafficLoading,
      providerTrafficMax,
      trafficColors,
      loadProviderTraffic,
      canViewReport,
      reportSnapshot,
      reportLoading,
      reportScope,
      reportMetrics,
      tabs,
      filteredTabs,
      isLoading,
      error,
      user,
      notificationLoading,
      notificationPromptVisible,
      notificationState,
      attentionModules,
      attentionTotal,
      accessLabel,
      moduleIcon,
      moduleSearch,
      getProfileDisplay,
      handleEnableNotifications,
      handleRefresh,
      handleSignOut,
      navigateToModule,
      loadModules
    };
  }
};
</script>

<style scoped>
.plain-dashboard {
  box-sizing: border-box;
  margin: 0 auto;
  max-width: var(--ui-content-width);
  padding: 2.5rem 0 3rem;
  width: calc(100% - 4rem);
}

.plain-dashboard-heading,
.plain-section-heading,
.plain-dashboard-actions,
.plain-metric-label,
.plain-module-card-top {
  align-items: center;
  display: flex;
}

.plain-dashboard-heading,
.plain-section-heading {
  gap: 1.5rem;
  justify-content: space-between;
}

.plain-dashboard-heading { margin-bottom: 2.25rem; }
.plain-dashboard-heading h1,
.plain-section-heading h2,
.plain-module-copy h3 {
  color: var(--ui-foreground);
  font-family: var(--ui-font-display);
  font-weight: 700;
  letter-spacing: -.025em;
  margin: 0;
}
.plain-dashboard-heading h1 { font-size: 1.875rem; }
.plain-dashboard-heading p,
.plain-section-heading p,
.plain-module-copy p,
.plain-metric-card p,
.plain-state-card p,
.plain-empty-state p {
  color: var(--ui-muted-foreground);
  margin: .35rem 0 0;
}
.plain-dashboard-actions { gap: .6rem; }

.plain-button {
  align-items: center;
  border-radius: calc(var(--ui-radius) - 2px);
  display: inline-flex;
  font: inherit;
  font-size: .82rem;
  font-weight: 600;
  gap: .5rem;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 .9rem;
}
.plain-button-primary { background: var(--ui-primary); border: 1px solid var(--ui-primary); color: var(--ui-primary-foreground); }
.plain-button-outline { background: var(--ui-background); border: 1px solid var(--ui-border); color: var(--ui-foreground); }
.plain-button-outline:hover { background: var(--ui-accent); }

.plain-metric-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 1.25rem;
}
.plain-metric-card,
.plain-module-card,
.plain-state-card,
.plain-empty-state {
  background: var(--ui-card);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  box-shadow: var(--ui-shadow-xs);
  color: var(--ui-card-foreground);
}
.plain-metric-card { min-height: 11.75rem; padding: 1.75rem; }
.plain-metric-label { color: var(--ui-muted-foreground); justify-content: space-between; }
.plain-metric-label span { color: var(--ui-foreground); font-size: .85rem; font-weight: 600; }
.plain-metric-card > strong { display: block; font-size: 1.85rem; letter-spacing: -.03em; margin-top: 2.25rem; }
.plain-metric-card > strong.is-text { font-size: 1.25rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.plain-metric-card p { font-size: .78rem; }
.provider-traffic-card { align-items: center; box-sizing: border-box; display: grid; gap: 2rem; grid-template-columns: minmax(12rem, 1fr) minmax(0, 2fr); margin-bottom: 1.25rem; }
.provider-traffic-summary > strong { display: block; font-size: 1.85rem; letter-spacing: -.03em; margin-top: 1rem; }
.provider-traffic-summary small { color: var(--ui-muted-foreground); display: block; font-size: .65rem; line-height: 1.5; margin-top: .6rem; }
.provider-traffic-summary svg { color: #0d9488; }
.provider-traffic-bars { display: grid; gap: .3rem; min-width: 0; }
.provider-traffic-row { align-items: center; display: grid; font-size: .72rem; gap: .75rem; grid-template-columns: 4rem minmax(0, 1fr) 2.5rem; line-height: 1.1rem; }
.provider-traffic-row > span { color: var(--ui-muted-foreground); }
.provider-traffic-row > strong { font-size: .72rem; text-align: right; }
.provider-traffic-track { background: var(--ui-muted); border-radius: 999px; height: .55rem; overflow: hidden; }
.provider-traffic-track > span { border-radius: inherit; display: block; height: 100%; }
.provider-traffic-empty { color: var(--ui-muted-foreground); font-size: .75rem; }
@media (max-width: 650px) { .provider-traffic-card { gap: 1.25rem; grid-template-columns: minmax(0, 1fr); } }
.dashboard-report-caption { color: var(--ui-muted-foreground); display: flex; flex-wrap: wrap; font-size: .75rem; gap: .5rem 1rem; justify-content: space-between; margin-bottom: .75rem; }
.dashboard-report-card { --report-accent: #2563eb; box-sizing: border-box; display: block; min-width: 0; position: relative; text-decoration: none; }
.dashboard-report-card:hover { border-color: var(--report-accent); }
.dashboard-report-card:focus-visible { outline: 2px solid var(--ui-ring); outline-offset: 3px; }
.dashboard-report-card, .dashboard-report-card * { text-decoration: none !important; }
.dashboard-report-card::before { background: var(--report-accent); border-radius: 2px; content: ""; height: 3px; left: 1.75rem; position: absolute; right: 1.75rem; top: 0; opacity: .65; }
.dashboard-report-card.is-premium { --report-accent: #0d9488; }
.dashboard-report-card.is-utilization { --report-accent: #d97706; }
.dashboard-report-card.is-mlr { --report-accent: #64748b; }
.dashboard-report-card.is-risk { --report-accent: #dc2626; }
.dashboard-report-card .plain-metric-label > svg { color: var(--report-accent); }
.dashboard-report-card > strong { font-size: clamp(1.05rem, 1.4vw, 1.5rem); overflow-wrap: anywhere; }

.plain-workspace-section { padding-top: .75rem; }
.plain-section-heading { border-bottom: 1px solid var(--ui-border); padding: 1rem 0 1.25rem; }
.plain-section-heading h2 { font-size: 1.25rem; }
.plain-section-heading p { font-size: .9rem; }
.plain-module-filter { align-items: center; color: var(--ui-muted-foreground); display: flex; position: relative; width: min(20rem, 100%); }
.plain-module-filter svg { left: .75rem; position: absolute; }
.plain-module-filter input {
  background: var(--ui-background);
  border: 1px solid var(--ui-input);
  border-radius: calc(var(--ui-radius) - 2px);
  color: var(--ui-foreground);
  font: inherit;
  height: 2.6rem;
  padding: 0 .75rem 0 2.35rem;
  width: 100%;
}

.plain-module-grid { display: grid; gap: 1rem; grid-template-columns: repeat(3, minmax(0, 1fr)); padding-top: 1.25rem; }
.plain-module-card { display: flex; flex-direction: column; min-height: 14.25rem; padding: 1.5rem; }
.plain-module-card-top { justify-content: space-between; }
.plain-module-icon { align-items: center; background: var(--ui-secondary); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-secondary-foreground); display: flex; height: 3rem; justify-content: center; width: 3rem; }
.plain-open-button { min-height: 2.35rem; padding: 0 .85rem; }
.plain-module-copy { margin-top: 2.4rem; }
.plain-module-copy h3 { font-size: 1rem; }
.plain-module-copy p { font-size: .88rem; line-height: 1.5; }
.plain-module-notice { align-self: flex-start; background: #fef3c7; border: 1px solid #f5c451; border-radius: 999px; color: #854d0e; font-size: .7rem; font-weight: 600; margin-top: auto; max-width: 100%; overflow: hidden; padding: .3rem .55rem; text-overflow: ellipsis; white-space: nowrap; }

.plain-state-card,
.plain-empty-state { align-items: center; display: flex; flex-direction: column; justify-content: center; min-height: 20rem; padding: 2rem; text-align: center; }
.plain-state-card strong, .plain-empty-state h3 { font-family: var(--ui-font-display); margin: .9rem 0 0; }
.plain-state-card .plain-button { margin-top: 1rem; }
.plain-state-card.is-error svg { color: var(--ui-destructive); }
.plain-spinner { animation: plain-spin .7s linear infinite; border: 2px solid var(--ui-border); border-radius: 999px; border-top-color: var(--ui-primary); height: 2rem; width: 2rem; }
@keyframes plain-spin { to { transform: rotate(360deg); } }

@media (max-width: 1100px) {
  .plain-metric-grid, .plain-module-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 700px) {
  .plain-dashboard { padding: 1.5rem 0 2rem; width: calc(100% - 2rem); }
  .plain-dashboard-heading, .plain-section-heading { align-items: stretch; flex-direction: column; }
  .plain-dashboard-actions { align-items: stretch; }
  .plain-dashboard-actions .plain-button { flex: 1; }
  .plain-metric-grid, .plain-module-grid { grid-template-columns: 1fr; }
  .plain-module-filter { width: 100%; }
}
</style>
